import React, { useEffect, useRef } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { FaGavel, FaQuoteLeft, FaMountain } from 'react-icons/fa';
import '../styles/AboutSection.css'; // We will revise this file

// --- DATA STRUCTURE (No changes needed, it's perfect) ---
const aboutCards = [
  {
    id: 'photo',
    type: 'photo',
    gridClass: 'is-tall-photo',
    imageUrl: '/assets/about-img.webp', // Make sure this path is correct
    authorName: 'Robert Falkenberg'
  },
  {
    id: 'quote',
    type: 'quote',
    gridClass: 'is-tall-quote',
    quote: "A novel should entertain the reader and evoke emotions-joy, fear, suspense, sadness-but that's not all. It should also examine and reflect on the world around us, and keep us thinking after we turn the final page.",
    author: "Robert Falkenberg"
  },
  {
    id: 'info-legal',
    type: 'info',
    gridClass: 'is-wide-info',
    icon: <FaGavel />,
    title: 'A Calling Rediscovered',
    description: `I wanted to be a lot of things. But I saw Point Break when I was 15 and decided one day I'd go to law school and be in the FBI like Johnny Utah. He was so cool. Ultimately, I worked in court administration after college, heard a judge speak, and decided to do that instead. After 13 years of legal practice and forgetting about ever being a judge, I actually became one. I am proud of my public service, and I am privileged to have the opportunity. The change in my career path from lawyer to judge provided me with a more consistent schedule, allowing me to devote more personal time to writing novels as a professional. I am, however, nowhere near as cool as Johnny Utah.`
  },
  {
    id: 'stat-writing',
    type: 'stat',
    gridClass: 'is-stat-writing',
    value: 20,
    label: 'Years of Writing',
  },
  {
    id: 'stat-projects',
    type: 'stat',
    gridClass: 'is-stat-projects',
    value: 20,
    label: 'Speaking Engagements',
  },
  {
    id: 'ticker',
    type: 'ticker',
    gridClass: 'is-wide-ticker',
    items: ['Author', 'Novelist', 'Story Writer', 'Lawyer', 'Judge','Author', 'Novelist', 'Story Writer', 'Lawyer', 'Judge']
  },
  {
    id: 'info-personal',
    type: 'info',
    gridClass: 'is-tall-personal',
    icon: <FaMountain />,
    title: 'Personal Life',
    description: 'Family time reigns over all other hobbies I have. Time with my wife and children is priceless. I have read books and literature voraciously all my life. My first novel was Watership Down by Richard Adams-I read it in the third grade from the school library. I started reading Stephen King in elementary school as well. Lonesome Dove by Larry McMurtry is my all-time favorite. I enjoy ultra running/hiking and camping, especially in the mountain and forest backcountry. Gardening has always brought me peace. Like writing a novel, growing a mature garden from seeds or small plants is a trilogy of patience, madness, and passion. Other times I might have a guitar or bass in my hands, performing songs with my musical wife and children.'
  },
  {
    id: 'cta',
    type: 'cta',
    gridClass: 'is-full-cta',
    title: "Have a story to tell?",
    description: "Whether starting from scratch or looking for a collaborator, let's create something impactful.",
    buttonText: "Let's Connect",
    link: "mailto:robertfalkenbergauthor@gmail.com" 
  },
];


// --- REUSABLE COMPONENTS (No changes needed) ---

const AnimatedCounter = ({ value }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView) {
      const controls = animate(0, value, {
        duration: 2,
        ease: 'easeOut',
        onUpdate: (latest) => {
          if (ref.current) {
            ref.current.textContent = Math.round(latest);
          }
        },
      });
      return () => controls.stop();
    }
  }, [isInView, value]);

  return <span ref={ref}>0</span>;
};

const CardTicker = ({ items }) => {
  const extendedItems = [...items, ...items, ...items, ...items, ...items, ...items, ...items, ...items];
  return (
    <div className="bento-card-ticker-wrap">
      <motion.div
        className="bento-card-ticker-track"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ ease: 'linear', duration: 35, repeat: Infinity }}
      >
        {extendedItems.map((item, i) => (
          <span key={i} className="bento-card-ticker-item">{item}</span>
        ))}
      </motion.div>
    </div>
  );
};


// --- MAIN COMPONENT (No changes needed) ---

export default function AboutSection() {
  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const renderCardContent = (card) => {
    switch (card.type) {
      case 'photo':
        return (
          <>
            <img src={card.imageUrl} alt="Author Robert Falkenberg" className="bento-image" />
            <div className="photo-overlay-name">{card.authorName}</div>
          </>
        );
      case 'quote':
        return (
          <div className="bento-quote-content">
            <FaQuoteLeft className="quote-icon-bg" />
            <p>"{card.quote}"</p>
            <span>— {card.author}</span>
          </div>
        );
      case 'stat':
        return (
          <div className="bento-stat-content">
            <div className="stat-value"><AnimatedCounter value={card.value} />+</div>
            <div className="stat-label">{card.label}</div>
          </div>
        );
      case 'info':
        return (
          <div className="bento-info-content">
            <div className="info-icon-corner">{card.icon}</div>
            <h4>{card.title}</h4>
            <p>{card.description}</p>
          </div>
        );
      case 'ticker':
        return <CardTicker items={card.items} />;
      case 'cta':
        return (
          <div className="bento-cta-content">
            <h3>{card.title}</h3>
            <p>{card.description}</p>
            <a href={card.link}>{card.buttonText}</a>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section id="about" className="about-bento-section">
      <motion.h2
        className="bento-main-title"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        The Mind Behind the Words
      </motion.h2>
      <div className="about-bento-grid">
        {aboutCards.map(card => (
          <motion.div
            key={card.id}
            className={`bento-card card-type-${card.type} ${card.gridClass}`}
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
          >
            {renderCardContent(card)}
          </motion.div>
        ))}
      </div>
    </section>
  );
}